/**
 * RPM in-service policy: whether a patient is out of the RPM program
 * (discharged/closed) as of a given calendar date, evaluated from program
 * session dates — not merely the current status — so backdated encounters and
 * later re-enrollments are judged against the right window.
 *
 * Eligible ("in service") states as of date D: Active, On Hold, Outreach,
 * Pre-Service. Out of service: Discharged or Closed as of D with no open or
 * pending session.
 *
 * Mirror of functions/utils/rpmServicePolicy.js — keep in sync.
 */

function ymdOf(value) {
  if (!value) return '';
  if (typeof value === 'string') return value.slice(0, 10);
  if (typeof value.toDate === 'function') {
    try {
      const d = value.toDate();
      return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    } catch {
      return '';
    }
  }
  return '';
}

export function isRpmProgram(program) {
  return String(program?.serviceTypeAbbreviation || '').toUpperCase() === 'RPM';
}

/**
 * @param {Array<object>} programs - patientPrograms docs
 * @param {string} ymd - encounter effective date 'YYYY-MM-DD'
 * @returns {{ outOfService: boolean, endDate: string|null, kind: 'discharged'|'closed'|null }}
 *   Fails open: any ambiguity or missing data → in service.
 */
export function rpmOutOfServiceInfo(programs, ymd) {
  const inService = { outOfService: false, endDate: null, kind: null };
  if (!ymd || typeof ymd !== 'string') return inService;
  const rpm = (Array.isArray(programs) ? programs : []).filter(isRpmProgram);
  if (!rpm.length) return inService; // No RPM program — nothing to suppress.

  let latestEnd = null; // { endDate, kind }
  for (const program of rpm) {
    const sessions = Array.isArray(program.sessions) ? program.sessions : [];
    if (!sessions.length) return inService; // Outreach with no session rows — eligible.
    for (const s of sessions) {
      const enroll = ymdOf(s?.enrollmentDate);
      const discharge = ymdOf(s?.dischargeDate);
      const closed = ymdOf(s?.closedDate);
      // The discharge/closed date itself is eligible for activity; suppression
      // begins the following calendar day.
      if (
        enroll
        && enroll <= ymd
        && (!discharge || discharge >= ymd)
        && (!closed || closed >= ymd)
      ) return inService;
      // Unstarted session (outreach / pre-service / future re-enroll) not yet
      // closed as of ymd → eligible.
      if ((!enroll || enroll > ymd) && (!closed || closed > ymd)) return inService;
      // Session ended before ymd — track the latest end for messaging.
      const end = closed && closed < ymd
        ? { endDate: closed, kind: 'closed' }
        : discharge && discharge < ymd
          ? { endDate: discharge, kind: 'discharged' }
          : null;
      if (end && (!latestEnd || end.endDate > latestEnd.endDate)) latestEnd = end;
    }
  }
  if (!latestEnd) return inService;
  return { outOfService: true, endDate: latestEnd.endDate, kind: latestEnd.kind };
}

/** Encounter types never suppressed (transition notes must keep their minutes). */
export const SUPPRESSION_EXEMPT_TYPES = new Set(['program_status_change']);

/** Tenant feature flag (default OFF until validated per tenant). */
export function isSuppressionEnabled(tenantData) {
  return tenantData?.billingConfig?.suppressOutOfServiceMinutes === true;
}
